/**
 * Documents de l'entreprise (décision du propriétaire du 15 octobre 2026) :
 * adresses, types et lecture partagés par la liste, la page de lecture, la page
 * de saisie, la carte d'accueil, le Menu et le menu du compte.
 */
import { useQuery } from '@tanstack/react-query';
import type { CompanyDocumentKind } from '@myequip/shared';
import { api } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';

export const COMPANY_DOCUMENTS_PATH = '/entreprise/documents';
export const companyDocumentPath = (id: string): string => `${COMPANY_DOCUMENTS_PATH}/${id}`;

/** à lire / nouvelle version à lire / lu / simple consultation */
export type ReadStatus = 'a_lire' | 'nouvelle_version' | 'lu' | 'consultation';

export interface CompanyDocumentItem {
  id: string;
  kind: CompanyDocumentKind;
  title: string;
  requiresAck: boolean;
  archivedAt: string | null;
  hasEssential: boolean;
  version: { id: string; number: number; publishedAt: string };
  myStatus: ReadStatus;
  myAckAt: string | null;
  /** Qui gère seulement : lectures de la version en vigueur par les membres actifs. */
  readCount?: number;
}

export interface CompanyDocumentList {
  canManage: boolean;
  teamSize: number | null;
  items: CompanyDocumentItem[];
}

export interface CompanyDocumentDetail {
  id: string;
  kind: CompanyDocumentKind;
  title: string;
  requiresAck: boolean;
  archivedAt: string | null;
  canManage: boolean;
  lastReminderAt: string | null;
  /** Qui gère seulement : jeton de modification à renvoyer tel quel (`expectedUpdatedAt`). */
  updatedAt: string | null;
  current: {
    id: string;
    number: number;
    essential: string | null;
    publishedAt: string;
    publishedBy: string;
    fileId: string;
    fileName: string;
    fileSize: number;
    pdfUrl: string;
  };
  myAckAt: string | null;
  versions: {
    id: string;
    number: number;
    title: string;
    essential: string | null;
    publishedAt: string;
    publishedBy: string;
    fileName: string;
    pdfUrl: string;
  }[];
}

export const toRead = (item: CompanyDocumentItem): boolean =>
  !item.archivedAt && (item.myStatus === 'a_lire' || item.myStatus === 'nouvelle_version');

/** Liste des documents, partagée par toutes les surfaces (une seule requête en cache). */
export function useCompanyDocuments(enabled = true) {
  const { me } = useAuth();
  return useQuery({
    queryKey: ['company-documents', me?.id],
    queryFn: () => api<CompanyDocumentList>('/company-documents'),
    enabled: enabled && Boolean(me),
    staleTime: 60_000,
  });
}
